import axios from "axios";
import { useEffect, useState } from "react";

// Shared API plumbing for the Invoice Bellin dashboard, following the
// terbebas prodpanel pattern: per-call axios factory carrying the JWT, no
// global interceptors.

export const API_BASE =
  process.env.REACT_APP_INVOICE_API ?? "http://localhost:3021/api";

// Shown verbatim in Pengaturan for the operator to paste into each gateway's
// dashboard, so they have to match the real routes (app.ts mounts the router
// at /api, and API_BASE already carries that prefix).
export const MIDTRANS_WEBHOOK_URL =
  API_BASE.replace(/\/+$/, "") + "/webhooks/midtrans";
export const ESPAY_INQUIRY_URL =
  API_BASE.replace(/\/+$/, "") + "/webhooks/espay/v1.0/transfer-va/inquiry";
export const ESPAY_PAYMENT_URL =
  API_BASE.replace(/\/+$/, "") + "/webhooks/espay/v1.0/transfer-va/payment";
export const XENDIT_WEBHOOK_URL =
  API_BASE.replace(/\/+$/, "") + "/webhooks/xendit";
export const DOKU_WEBHOOK_URL = API_BASE.replace(/\/+$/, "") + "/webhooks/doku";
export const FASPAY_WEBHOOK_URL =
  API_BASE.replace(/\/+$/, "") + "/webhooks/faspay";

// Own key so a localhost login never collides with the terbebas dashboards.
export const SESSION_KEY = "invoiceBellinAdminSession";

export interface Session {
  token: string;
  user: { id: string; name: string; email: string; role: "OWNER" | "ADMIN" };
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function api(session: Session) {
  const client = axios.create({
    baseURL: API_BASE,
    headers: { Authorization: `Bearer ${session.token}` },
  });
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Expired, disabled, or password-reset sessions must not leave an
      // employee looking at a stale dashboard. A 403 is deliberately not
      // handled here because it means the account is valid but lacks a role.
      if (error?.response?.status === 401 && localStorage.getItem(SESSION_KEY)) {
        localStorage.removeItem(SESSION_KEY);
        window.location.reload();
      }
      return Promise.reject(error);
    },
  );
  return client;
}

// Unauthenticated client for the /pay/* endpoints (public payment page).
export const publicApi = axios.create({ baseURL: API_BASE });

export function formatRupiah(value: number | string): string {
  return (
    "Rp " +
    Math.round(Number(value))
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );
}

// Compact table format requested by the invoice list: no gap between the
// currency prefix and the amount (for example, "Rp125,000").
export function formatRupiahCompact(value: number | string): string {
  return (
    "Rp" +
    Math.round(Number(value))
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );
}

export function formatTanggal(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
  );
}

// Invoice administration timestamps are always shown in Western Indonesian
// Time, independent of the operating system or browser locale.
export function formatTanggalWib(iso: string): string {
	const parts = formatTanggalWibParts(iso);
	return parts.valid ? `${parts.date} ${parts.time} WIB` : "—";
}

export function formatTanggalWibParts(iso: string): {
  date: string;
  time: string;
  valid: boolean;
} {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { date: "—", time: "", valid: false };

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jakarta",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return {
    date: `${value("day")}/${value("month")}/${value("year")}`,
    time: `${value("hour")}:${value("minute")}:${value("second")}`,
    valid: true,
  };
}

export type CountryCallingCode = "+62" | "+65" | "+60";

export const COUNTRY_CALLING_CODES: Array<{
  code: CountryCallingCode;
  label: string;
  minLength: number;
  maxLength: number;
  placeholder: string;
}> = [
  { code: "+62", label: "Indonesia (+62)", minLength: 9, maxLength: 12, placeholder: "812-3456-7890" },
  { code: "+65", label: "Singapore (+65)", minLength: 8, maxLength: 8, placeholder: "9123-4567" },
  { code: "+60", label: "Malaysia (+60)", minLength: 9, maxLength: 10, placeholder: "12-345-6789" },
];

function countryRule(code: CountryCallingCode) {
  return COUNTRY_CALLING_CODES.find((item) => item.code === code)!;
}

function groupPhoneLocal(localDigits: string, code: CountryCallingCode): string {
  if (code === "+65") {
    return localDigits.length <= 4
      ? localDigits
      : `${localDigits.slice(0, 4)}-${localDigits.slice(4, 8)}`;
  }
  if (code === "+60") {
    if (localDigits.length <= 2) return localDigits;
    if (localDigits.length <= 5) return `${localDigits.slice(0, 2)}-${localDigits.slice(2)}`;
    return `${localDigits.slice(0, 2)}-${localDigits.slice(2, 5)}-${localDigits.slice(5)}`;
  }
  if (localDigits.length <= 3) return localDigits;
  if (localDigits.length <= 7) return `${localDigits.slice(0, 3)}-${localDigits.slice(3)}`;
  return `${localDigits.slice(0, 3)}-${localDigits.slice(3, 7)}-${localDigits.slice(7)}`;
}

export function phoneLocalDigits(value: string, code: CountryCallingCode): string {
  let digits = value.replace(/\D/g, "");
  const callingCode = code.slice(1);
  if (digits.startsWith(callingCode)) digits = digits.slice(callingCode.length);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return digits.slice(0, countryRule(code).maxLength);
}

export function formatPhoneLocal(value: string, code: CountryCallingCode): string {
  return groupPhoneLocal(phoneLocalDigits(value, code), code);
}

export function buildInternationalPhone(code: CountryCallingCode, localValue: string): string {
  return code + phoneLocalDigits(localValue, code);
}

export function isValidInternationalPhone(code: CountryCallingCode, localValue: string): boolean {
  const length = phoneLocalDigits(localValue, code).length;
  const rule = countryRule(code);
  return length >= rule.minLength && length <= rule.maxLength;
}

export function formatPhoneDisplay(value: string): string {
  let digits = value.replace(/\D/g, "");
  if (!digits) return "—";
  if (digits.startsWith("0")) digits = "62" + digits.slice(1);

  const matched = COUNTRY_CALLING_CODES.find((item) => digits.startsWith(item.code.slice(1)));
  if (!matched) return value;
  const callingCode = matched.code.slice(1);
  const local = digits.slice(callingCode.length, callingCode.length + matched.maxLength);
  return `${matched.code} ${groupPhoneLocal(local, matched.code)}`;
}

export function isValidStoredPhone(value: string): boolean {
  let digits = value.replace(/\D/g, "");
  if (!digits) return false;
  if (digits.startsWith("0")) digits = "62" + digits.slice(1);

  const matched = COUNTRY_CALLING_CODES.find((item) => digits.startsWith(item.code.slice(1)));
  if (!matched) return false;
  const localLength = digits.length - matched.code.slice(1).length;
  return localLength >= matched.minLength && localLength <= matched.maxLength;
}

// Debounces a fast-changing value (search inputs) so filters re-run once per
// pause instead of per keystroke.
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export type Provider =
  "MIDTRANS" | "ESPAY" | "XENDIT" | "DOKU" | "FASPAY" | "DEMO";

export interface GatewaySummary {
  id: string;
  name: string;
  provider: Provider;
}

export interface AdminSummary {
  id: string;
  name: string;
  email: string;
  role: "OWNER" | "ADMIN";
}

export interface Invoice {
  id: string;
  invoiceNumber: number;
  customerName: string;
  phone: string;
  amount: string;
  bankName: string;
  virtualAccountNumber: string;
  bcaVirtualAccountNumber: string;
  mandiriVirtualAccountNumber: string;
  note: string;
  vaDurationHours: number | null;
  status: "PENDING" | "PAID" | "EXPIRED" | "VOIDED";
  gatewayId: string;
  gateway: GatewaySummary;
  paymentProvider: string | null;
  paymentReference: string | null;
  paidAt: string | null;
  version: number;
  createdBy: AdminSummary | null;
  updatedBy: AdminSummary | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserSummary extends AdminSummary {
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLog {
  id: string;
  actorId: string | null;
  actorName: string;
  actorEmail: string;
  action: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  metadata: Record<string, unknown>;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  actor: (AdminUserSummary & { isActive: boolean }) | null;
}

// Public payment link for an invoice, shareable via WhatsApp.
export function paymentLink(invoiceId: string): string {
  return window.location.origin + "/pay/" + invoiceId;
}

// wa.me link with the payment URL prefilled. Normalises 08xx to 628xx.
export function whatsappLink(
  phone: string,
  invoice: { invoiceNumber: number; amount: string | number; id: string; gateway?: { provider: string } },
): string {
  const digits = phone.replace(/\D/g, "");
  const intl = digits.startsWith("0") ? "62" + digits.slice(1) : digits;
  const text =
    (invoice.gateway?.provider === 'DEMO' ? 'DEMO — bukan tagihan nyata. Jangan melakukan transfer.\n' : '') +
    `Halo, berikut link pembayaran Invoice #${invoice.invoiceNumber} ` +
    `sebesar ${formatRupiah(invoice.amount)}: ${paymentLink(invoice.id)}`;
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
}

export const PROVIDER_LABELS: Record<string, string> = {
  MIDTRANS: "Midtrans",
  ESPAY: "ESPAY",
  XENDIT: "Xendit",
  DOKU: "DOKU",
  FASPAY: "Faspay",
  DEMO: "Demo Payment",
};
